import { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { translations, supportedLanguages, hasLocale, ensureLocale } from '../i18n'
import { parseLocalePathname } from '../lib/localeSeoRoutes'

const IS_DEV = import.meta.env.DEV

/** When Provider is missing (e.g. HMR), still expose real language list so selectors do not crash. */
const SUPPORTED_FALLBACK = supportedLanguages

const STORAGE_KEY = 'app_lang'

const LanguageContext = createContext(null)

function getStoredLanguage() {
  if (typeof window === 'undefined') return 'en'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && hasLocale(stored)) return stored
  } catch (_) {}
  return 'en'
}

function getInitialLanguage() {
  if (typeof window === 'undefined') return 'en'
  const { locale } = parseLocalePathname(window.location.pathname)
  if (hasLocale(locale)) return locale
  return getStoredLanguage()
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage)
  const [localeReady, setLocaleReady] = useState(() => language === 'en' || !!translations[language])

  useEffect(() => {
    let cancelled = false
    ensureLocale(language)
      .then(() => {
        if (!cancelled) setLocaleReady(true)
      })
      .catch(() => {
        if (!cancelled) setLocaleReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [language])

  useEffect(() => {
    if (IS_DEV) console.debug('[LanguageContext] Current language state:', language)
  }, [language])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (language === 'ar') {
      root.setAttribute('dir', 'rtl')
      root.setAttribute('lang', 'ar')
    } else {
      root.setAttribute('dir', 'ltr')
      root.setAttribute('lang', language === 'en' ? 'en' : language)
    }
  }, [language])

  const setLanguage = useCallback((lang) => {
    if (!hasLocale(lang)) {
      if (IS_DEV) console.warn('[LanguageContext] Ignoring setLanguage ,  no translations for', lang)
      return
    }
    ensureLocale(lang)
      .then(() => {
        setLanguageState(lang)
        setLocaleReady(true)
        try {
          localStorage.setItem(STORAGE_KEY, lang)
        } catch (_) {}
        if (IS_DEV) console.debug('[LanguageContext] Language updated to', lang)
      })
      .catch(() => {})
  }, [])

  const t = useCallback(
    (key, params) => {
      let value = translations[language]?.[key] ?? translations.en?.[key] ?? key
      if (params && typeof value === 'string') {
        value = value.replace(/\{\{(\w+)\}\}/g, (_, k) =>
          params[k] !== undefined && params[k] !== null ? String(params[k]) : `{{${k}}}`
        )
      }
      return value
    },
    [language]
  )

  const value = useMemo(
    () => ({ language, setLanguage, t, supportedLanguages }),
    [language, setLanguage, t]
  )

  return (
    <LanguageContext.Provider value={value}>
      {localeReady ? children : null}
    </LanguageContext.Provider>
  )
}

const FALLBACK_CONTEXT = {
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
  supportedLanguages: SUPPORTED_FALLBACK,
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    if (IS_DEV) {
      console.warn('[useLanguage] Context not found ,  returning fallback. This usually means a Vite HMR module split occurred. Refreshing the page will fix it.')
    }
    return FALLBACK_CONTEXT
  }
  return ctx
}

// Prevent Vite HMR from splitting the context instance across hot reloads.
// A full page reload is safer than a partial module swap for context providers.
if (import.meta.hot) {
  import.meta.hot.decline()
}
